import { userFacingFeatures } from '@/shared/constants';
import type { SearchPreferences, SearchPreferenceTabKey } from '@/types';

export type { SearchPreferences } from '@/types';

export interface ResolvedSearchPreferences {
    defaultTab: SearchPreferenceTabKey;
    filters: {
        preserveOnTabChange: boolean;
    };
    results: {
        groupRelatedListings: boolean;
        includePublicCompanies: boolean;
        pageSize: 10 | 25 | 50;
        showRecentSearches: boolean;
    };
    tabs: {
        all: boolean;
        companies: boolean;
        documents: boolean;
        economic: boolean;
        events: boolean;
        news: boolean;
        trade: boolean;
        people: boolean;
        workspace: boolean;
    };
}

export const defaultSearchPreferences: ResolvedSearchPreferences = {
    defaultTab: 'companies',
    filters: {
        preserveOnTabChange: true,
    },
    results: {
        groupRelatedListings: true,
        includePublicCompanies: true,
        pageSize: 50,
        showRecentSearches: true,
    },
    tabs: {
        all: true,
        companies: true,
        documents: true,
        economic: true,
        events: false,
        news: true,
        trade: false,
        people: true,
        workspace: true,
    },
};

export const resolveSearchPreferences = (preferences?: SearchPreferences | null): ResolvedSearchPreferences => {
    const tabs = {
        all: preferences?.tabs?.all ?? defaultSearchPreferences.tabs.all,
        companies: preferences?.tabs?.companies ?? defaultSearchPreferences.tabs.companies,
        documents: preferences?.tabs?.documents ?? defaultSearchPreferences.tabs.documents,
        economic: preferences?.tabs?.economic ?? defaultSearchPreferences.tabs.economic,
        events:
            userFacingFeatures.marketEventsSearch &&
            (preferences?.tabs?.events ?? defaultSearchPreferences.tabs.events),
        news: preferences?.tabs?.news ?? defaultSearchPreferences.tabs.news,
        trade:
            userFacingFeatures.globalTradeSearch && (preferences?.tabs?.trade ?? defaultSearchPreferences.tabs.trade),
        people: preferences?.tabs?.people ?? defaultSearchPreferences.tabs.people,
        workspace: preferences?.tabs?.workspace ?? defaultSearchPreferences.tabs.workspace,
    };
    const requestedDefaultTab = preferences?.defaultTab ?? defaultSearchPreferences.defaultTab;
    const defaultTab = tabs[requestedDefaultTab]
        ? requestedDefaultTab
        : tabs[defaultSearchPreferences.defaultTab]
          ? defaultSearchPreferences.defaultTab
          : ((Object.keys(tabs) as SearchPreferenceTabKey[]).find((tab) => tabs[tab]) ??
            defaultSearchPreferences.defaultTab);

    return {
        defaultTab,
        filters: {
            preserveOnTabChange:
                preferences?.filters?.preserveOnTabChange ?? defaultSearchPreferences.filters.preserveOnTabChange,
        },
        results: {
            groupRelatedListings:
                preferences?.results?.groupRelatedListings ?? defaultSearchPreferences.results.groupRelatedListings,
            includePublicCompanies:
                preferences?.results?.includePublicCompanies ?? defaultSearchPreferences.results.includePublicCompanies,
            pageSize: preferences?.results?.pageSize ?? defaultSearchPreferences.results.pageSize,
            showRecentSearches:
                preferences?.results?.showRecentSearches ?? defaultSearchPreferences.results.showRecentSearches,
        },
        tabs,
    };
};
